import React, { useState } from 'react'
import { PageHeader } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'
import { useMyCommissions as useCommissions } from '@/hooks/useCommission'

type CommissionStatus = 'all' | 'pending' | 'earned' | 'paid'

export const AdminCommissionsPage: React.FC = () => {
    const { user } = useAuth()
    const { data: commissions, isLoading } = useCommissions(user?.id || '')
    const [statusFilter, setStatusFilter] = useState<CommissionStatus>('all')

    const filteredCommissions = commissions?.filter((c: any) => {
        return statusFilter === 'all' || c.status === statusFilter
    }) || []

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-amber-500 text-white'
            case 'earned': return 'bg-indigo-500 text-white'
            case 'paid': return 'bg-emerald-500 text-white'
            default: return 'bg-slate-400 text-white'
        }
    }

    const totalPending = commissions?.filter((c: any) => c.status === 'pending')?.reduce((acc: number, c: any) => acc + (c.amount || 0), 0) || 0
    const totalEarned = commissions?.filter((c: any) => c.status === 'earned')?.reduce((acc: number, c: any) => acc + (c.amount || 0), 0) || 0
    const totalPaid = commissions?.filter((c: any) => c.status === 'paid')?.reduce((acc: number, c: any) => acc + (c.amount || 0), 0) || 0

    return (
        <div>
            <PageHeader
                title="Commissions"
                subtitle="View and manage commission ledger"
            />

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-yellow-400 text-[20px]">schedule</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Pending (30-day lock)</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-2xl font-bold">Rp {totalPending.toLocaleString('id-ID')}</p>
                </div>
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-indigo-400 text-[20px]">verified</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Ready to Pay</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-2xl font-bold">Rp {totalEarned.toLocaleString('id-ID')}</p>
                </div>
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-green-400 text-[20px]">check_circle</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Total Paid Out</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-2xl font-bold">Rp {totalPaid.toLocaleString('id-ID')}</p>
                </div>
            </div>

            {/* Status Filters */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 md:pb-0">
                {(['all', 'pending', 'earned', 'paid'] as CommissionStatus[]).map((status) => (
                    <button
                        key={status}
                        onClick={() => setStatusFilter(status)}
                        className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-colors whitespace-nowrap ${statusFilter === status
                            ? 'bg-primary text-white'
                            : 'bg-white dark:bg-surface-card text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-white/5'
                            }`}
                    >
                        {status}
                    </button>
                ))}
            </div>

            {/* Commissions Table */}
            <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[500px]">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-white/5">
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">User</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden md:table-cell">Type</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Course</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Amount</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Status</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden md:table-cell">Date</th>
                            </tr>
                        </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-white/5">
                        {isLoading ? (
                            <tr>
                                <td colSpan={6} className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">Loading...</td>
                            </tr>
                        ) : filteredCommissions.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">No commissions found</td>
                            </tr>
                        ) : (
                            filteredCommissions.map((c: any) => (
                                <tr key={c.id} className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="size-10 rounded-full bg-gradient-to-br from-primary/40 to-primary/20 flex items-center justify-center">
                                                <span className="text-sm font-bold text-red-300">{c.userName?.charAt(0) || 'U'}</span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-gray-900 dark:text-white">{c.userName || 'User'}</p>
                                                <p className="text-xs text-gray-500 dark:text-text-muted">{c.userEmail || 'email@example.com'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 hidden md:table-cell">
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${c.commissionType === 'instructor' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-purple-500/20 text-purple-400'
                                            }`}>
                                            {c.commissionType || 'instructor'}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white truncate max-w-[150px]">
                                        {c.courseName || 'Course Name'}
                                    </td>
                                    <td className="px-5 py-4 text-sm font-bold text-red-400">
                                        Rp {(c.amount || 0).toLocaleString('id-ID')}
                                    </td>
                                    <td className="px-5 py-4">
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold uppercase ${getStatusBadgeColor(c.status)}`}>
                                            {c.status}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted hidden md:table-cell">
                                        {c.createdAt || '2025-01-15'}
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default AdminCommissionsPage
